import { EXTERNAL_LEAGUE_CODE } from "../../constants/leagues";
import {
  ACCOUNTING_EXTERNAL_LEAGUE_LEVELS,
  leagueTakesExternalMatchesInAccounting,
  resolveAccountingLeagueId,
} from "../../supabase/functions/_shared/accountingLeagueScope";

/**
 * Client-side mirror of the league scope the match RPCs apply
 * (`get_league_matches` / `get_all_matches_optimized`):
 *
 *   (NOT m.is_external_competition AND m.league_group_id IN <groups of the league>)
 *   OR
 *   (m.is_external_competition AND (m.originating_league_id = <league>
 *       OR (m.home_team_id OR m.away_team_id) IN <teams of the league's groups, same season>))
 *
 * An external competition match (Coupe d'Algérie…) is handled by the league that
 * organises it (`originating_league_id`) — see getMatchHandlingLeagueId — and is
 * also listed (read-only) by every league one of whose teams plays it (2026-10-16:
 * LIRF sees the Annaba-organised rounds of its clubs).
 *
 * Direct PostgREST reads used to OR in `is_external_competition.eq.true`, which
 * returned every league's external (FAF cup…) matches.
 */

/**
 * Above this many team ids, inlining `home_team_id.in.(…)` + `away_team_id.in.(…)`
 * pushes the request URL past ~22 KB (the gateway rejects ~33 KB). Past it the
 * team branch is dropped (organised matches only).
 */
export const MAX_INLINE_TEAM_IDS = 300;

export interface LeagueMatchScope {
  /** League groups (of the selected season) whose regular matches are in scope. */
  leagueGroupIds: readonly string[];
  /** Teams placed in those groups — decides which external matches are in scope. */
  leagueTeamIds?: readonly string[] | null;
  /** League whose organised external matches are in scope. */
  leagueId?: string | null;
}

const uniq = (ids: readonly (string | null | undefined)[] | null | undefined): string[] =>
  Array.from(new Set((ids ?? []).filter((id): id is string => Boolean(id))));

/**
 * PostgREST `or=(…)` body for `query.or(...)`. Returns null when there is no
 * group to scope to (callers keep their existing "no filter" behaviour).
 */
export function buildLeagueMatchScopeFilter(scope: LeagueMatchScope): string | null {
  const groupIds = uniq(scope.leagueGroupIds);
  if (groupIds.length === 0) return null;

  const regular = `and(is_external_competition.eq.false,league_group_id.in.(${groupIds.join(',')}))`;

  const teamIds = uniq(scope.leagueTeamIds);
  const list = teamIds.join(',');
  const byTeams = `or(home_team_id.in.(${list}),away_team_id.in.(${list}))`;
  if (scope.leagueId) {
    const organised = `${regular},and(is_external_competition.eq.true,originating_league_id.eq.${scope.leagueId})`;
    if (teamIds.length === 0 || teamIds.length > MAX_INLINE_TEAM_IDS) return organised;
    return `${organised},and(is_external_competition.eq.true,${byTeams})`;
  }
  if (teamIds.length === 0) {
    // A league with groups but no team yet has no external match either.
    return regular;
  }
  return `${regular},and(is_external_competition.eq.true,${byTeams})`;
}

/** Union of the team ids of the given groups (`LeagueGroup.teamIds`). */
export function collectGroupTeamIds(
  groups: readonly { teamIds?: readonly string[] | null }[] | null | undefined,
): string[] {
  const out = new Set<string>();
  for (const g of groups ?? []) for (const id of g.teamIds ?? []) if (id) out.add(id);
  return Array.from(out);
}

export interface ScopableMatch {
  league_group_id?: string | null;
  is_external_competition?: boolean | null;
  originating_league_id?: string | null;
  home_team_id?: string | null;
  away_team_id?: string | null;
}

/** Same predicate, evaluated on rows already fetched. */
export function isMatchInLeagueScope(
  match: ScopableMatch,
  groupIds: ReadonlySet<string>,
  teamIds: ReadonlySet<string>,
  leagueId: string,
): boolean {
  if (match.is_external_competition) {
    if (match.originating_league_id === leagueId) return true;
    return (
      (!!match.home_team_id && teamIds.has(match.home_team_id)) ||
      (!!match.away_team_id && teamIds.has(match.away_team_id))
    );
  }
  if (match.is_external_competition === false) {
    return !!match.league_group_id && groupIds.has(match.league_group_id);
  }
  return false; // NULL flag: the RPC's NOT NULL / = true both yield NULL → excluded
}

/**
 * League that handles a match (roles, indemnities, designations, mission orders):
 * the organising league of an external competition match, else its group's league.
 * Same rule as SQL `match_membership_league_id` / `get_mission_order_details`.
 */
export function getMatchHandlingLeagueId(match: {
  isExternalCompetition?: boolean | null;
  originatingLeagueId?: string | null;
  leagueGroup?: { league?: { id?: string | null } | null } | null;
}): string | null {
  if (match.isExternalCompetition && match.originatingLeagueId) return match.originatingLeagueId;
  return match.leagueGroup?.league?.id ?? null;
}

// ─── Accounting league ────────────────────────────────────────

/**
 * League whose accounting a match belongs to (indemnities, kanban, payments,
 * virements, états, closures). Rule shared with the edge functions and the
 * accounting RPCs: supabase/functions/_shared/accountingLeagueScope.ts.
 *   - regular match: its group league;
 *   - external competition match: its organising league when that league is a
 *     regional (level 4, LRF_*) or wilaya (5, LWF_*) league, else null (LIRF,
 *     LNFA, LFP, federation… externals stay out of every accounting).
 * Differs from getMatchHandlingLeagueId only for non-regional externals.
 */
export { ACCOUNTING_EXTERNAL_LEAGUE_LEVELS, leagueTakesExternalMatchesInAccounting };

/** League id → level (`League.level`); undefined when unknown. */
export type LeagueLevelOf = (leagueId: string) => number | null | undefined;

export interface LeagueLevelRef {
  id: string;
  level?: number | null;
}

export function leagueLevelLookup(leagues: readonly LeagueLevelRef[] | null | undefined): LeagueLevelOf {
  const byId = new Map<string, number | null | undefined>();
  for (const l of leagues ?? []) if (l?.id) byId.set(l.id, l.level);
  return (leagueId) => byId.get(leagueId);
}

export function getMatchAccountingLeagueId(
  match: {
    isExternalCompetition?: boolean | null;
    originatingLeagueId?: string | null;
    leagueGroup?: { league?: { id?: string | null } | null } | null;
  },
  leagueLevelOf: LeagueLevelOf,
): string | null {
  return resolveAccountingLeagueId(
    {
      isExternalCompetition: match.isExternalCompetition,
      originatingLeagueId: match.originatingLeagueId,
      groupLeagueId: match.leagueGroup?.league?.id ?? null,
    },
    leagueLevelOf,
  );
}

export function isMatchInAccountingScope(
  match: Parameters<typeof getMatchAccountingLeagueId>[0],
  leagueId: string,
  leagueLevelOf: LeagueLevelOf,
): boolean {
  return getMatchAccountingLeagueId(match, leagueLevelOf) === leagueId;
}

/**
 * The virtual league holding the external competition groups — by code only: its
 * name is now « Coupe d'Algérie » (no longer « Compétitions Externes »), and a name
 * is not an identifier.
 */
export function isExternalCompetitionsLeague(league: { code?: string | null; name?: string | null }): boolean {
  return league.code === EXTERNAL_LEAGUE_CODE;
}

export interface AccountingLeagueFilter {
  /** `league_groups.league_id` values to accept (+ EXT_COMPETITIONS when a league takes externals). */
  groupLeagueIds: string[];
  /** Filter leagues (regional / wilaya) whose organised external matches are in scope. */
  externalOrganiserIds: string[];
}

/**
 * Server-side league filter of a direct PostgREST accounting read (payments,
 * virements…): the selected leagues' groups, plus — for a regional / wilaya
 * league — the EXT_COMPETITIONS groups narrowed by
 * buildAccountingExternalMatchesOrFilter to the externals it organises. Without
 * the league list (levels unknown) or the EXT league, nothing external is added
 * (the historical behaviour).
 */
export function buildAccountingLeagueFilter(
  leagueIds: readonly string[],
  leagues: readonly (LeagueLevelRef & { code?: string | null; name?: string | null })[] | null | undefined,
): AccountingLeagueFilter {
  const ids = uniq(leagueIds);
  const levelOf = leagueLevelLookup(leagues);
  const externalOrganiserIds = ids.filter((id) => leagueTakesExternalMatchesInAccounting(levelOf(id)));
  const externalLeagueIds = (leagues ?? []).filter(isExternalCompetitionsLeague).map((l) => l.id);
  if (externalOrganiserIds.length === 0 || externalLeagueIds.length === 0) {
    return { groupLeagueIds: ids, externalOrganiserIds: [] };
  }
  return { groupLeagueIds: uniq([...ids, ...externalLeagueIds]), externalOrganiserIds };
}

/**
 * `matches.or=(…)` body that goes with AccountingLeagueFilter.groupLeagueIds:
 * every regular match, external ones only when organised by one of the given
 * leagues. null = no external branch (no filter to add).
 */
export function buildAccountingExternalMatchesOrFilter(
  externalOrganiserIds: readonly string[] | null | undefined,
): string | null {
  const ids = uniq(externalOrganiserIds);
  if (ids.length === 0) return null;
  return `is_external_competition.eq.false,and(is_external_competition.eq.true,originating_league_id.in.(${ids.join(",")}))`;
}

/** Split a list for `.in()` calls that must stay under the URL limit. */
export function chunkIds<T>(ids: readonly T[], size = 150): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}
